import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { motion } from 'framer-motion';

const cookieOptions = [
  { id: 'necessary', label: 'Strictly Necessary Cookies', description: 'These cookies are essential for the website to function and cannot be switched off. They are usually set in response to actions made by you, such as setting your privacy preferences, logging in, or filling in forms.', disabled: true },
  { id: 'analytics', label: 'Analytics Cookies', description: 'These cookies allow us to count visits and traffic sources, so we can measure and improve the performance of our site. They help us know which pages are the most and least popular and see how visitors move around the site.' },
  { id: 'preferences', label: 'Preferences Cookies', description: 'These cookies enable the website to provide enhanced functionality and personalization. They may be set by us or by third-party providers whose services we have added to our pages.' },
  { id: 'marketing', label: 'Marketing Cookies', description: 'These cookies may be set through our site by our advertising partners. They may be used by those companies to build a profile of your interests and show you relevant adverts on other sites.' },
];

const CookiePreferencesModal = ({ isOpen, onClose, onSave, initialPreferences }) => {
  const [preferences, setPreferences] = useState(initialPreferences);

  useEffect(() => {
    setPreferences(initialPreferences);
  }, [initialPreferences, isOpen]);

  const handleCheckboxChange = (id) => {
    setPreferences(prev => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  const handleSavePreferences = () => {
    onSave(preferences);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[525px] bg-card border-border">
        <DialogHeader>
          <DialogTitle className="text-2xl font-heading text-primary-foreground">Cookie Preferences</DialogTitle>
          <DialogDescription className="text-muted-foreground">
            Manage your cookie settings. You can enable or disable different types of cookies below. For more details, please see our <a href="/cookies" className="text-primary hover:underline">Cookie Policy</a>.
          </DialogDescription>
        </DialogHeader>
        <motion.div 
          className="space-y-6 py-4 max-h-[60vh] overflow-y-auto pr-2"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.1, duration: 0.3 }}
        >
          {cookieOptions.map((option) => (
            <motion.div
              key={option.id}
              className="flex items-start space-x-3 p-4 bg-background rounded-md border border-border"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.1 + cookieOptions.indexOf(option) * 0.05 }}
            >
              <Checkbox
                id={option.id}
                checked={preferences[option.id]}
                onCheckedChange={() => handleCheckboxChange(option.id)}
                disabled={option.disabled}
                className="mt-1"
              />
              <div className="grid gap-1.5 leading-none">
                <Label htmlFor={option.id} className={`font-semibold ${option.disabled ? 'text-muted-foreground/70' : 'text-foreground'}`}>
                  {option.label}
                </Label>
                <p className="text-xs text-muted-foreground">{option.description}</p>
              </div>
            </motion.div>
          ))}
        </motion.div>
        <DialogFooter className="mt-6">
          <Button variant="outline" onClick={onClose} className="border-primary text-primary hover:bg-primary/10">
            Cancel
          </Button>
          <Button onClick={handleSavePreferences} className="bg-primary text-primary-foreground hover:bg-primary/90">
            Save Preferences
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CookiePreferencesModal;